import React, { useMemo } from "react";

/**
 * 轻量航线态势图：用真实 OD 数据（route_code 如 "HKG-LGG"）绘制 SVG 弧线网络。
 * - 已知机场使用真实经纬度，按当前网络自动取边界框缩放铺满画布；
 * - 未知机场用确定性散列落点，保证布局稳定可读；
 * - 弧线颜色按航班完成率走红黄绿，粗细按实际航段数。
 */

const AIRPORT_COORDS = {
    HKG: [113.91, 22.31],
    CGO: [113.84, 34.52],
    PVG: [121.81, 31.14],
    PEK: [116.41, 40.08],
    CAN: [113.3, 23.39],
    SZX: [113.81, 22.64],
    HGH: [120.43, 30.23],
    TAO: [120.37, 36.27],
    NKG: [118.86, 31.74],
    XIY: [108.75, 34.45],
    CTU: [103.95, 30.58],
    ICN: [126.45, 37.46],
    NRT: [140.39, 35.77],
    KIX: [135.24, 34.43],
    SIN: [103.99, 1.36],
    BKK: [100.75, 13.69],
    DEL: [77.1, 28.57],
    BOM: [72.87, 19.09],
    ALA: [77.04, 43.35],
    TAS: [69.28, 41.26],
    DXB: [55.36, 25.25],
    AUH: [54.65, 24.43],
    DOH: [51.61, 25.27],
    IST: [28.81, 41.26],
    SVO: [37.41, 55.97],
    LGG: [5.44, 50.64],
    BUD: [19.26, 47.44],
    LEJ: [12.24, 51.42],
    FRA: [8.57, 50.03],
    CDG: [2.55, 49.01],
    AMS: [4.76, 52.31],
    LHR: [-0.46, 51.47],
    LUX: [6.21, 49.63],
    MXP: [8.71, 45.63],
    LGG2: [5.44, 50.64],
    MAA: [80.17, 12.99],
    HHN: [7.26, 49.95],
    OST: [2.86, 51.2],
    VIE: [16.57, 48.11],
    PRG: [14.26, 50.1],
    JFK: [-73.78, 40.64],
    ORD: [-87.9, 41.98],
    LAX: [-118.41, 33.94],
    ANC: [-149.99, 61.17],
    MEX: [-99.07, 19.44],
    GRU: [-46.47, -23.43],
    NBO: [36.93, -1.32],
    JNB: [28.25, -26.13],
    ADD: [38.8, 8.98],
    LOS: [3.32, 6.58],
};

const VIEW_WIDTH = 760;
const VIEW_HEIGHT = 360;
const PAD = 56;

const parseRouteCode = (rawCode) => {
    if (!rawCode) return null;
    const code = String(rawCode).toUpperCase().trim();
    const tokens = code.split(/[^A-Z0-9]+/).filter(Boolean);
    let origin;
    let destination;
    if (tokens.length >= 2) {
        [origin, destination] = tokens;
    } else if (code.length >= 6) {
        origin = code.slice(0, 3);
        destination = code.slice(3, 6);
    } else {
        return null;
    }
    if (!origin || !destination || origin === destination) return null;
    return { origin: origin.slice(0, 3), destination: destination.slice(0, 3) };
};

const completionTone = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
        return { color: "#2b6cff", level: "info" };
    }
    const value = Number(rate);
    if (value >= 0.95) return { color: "#15a35a", level: "green" };
    if (value >= 0.9) return { color: "#d98a0b", level: "amber" };
    return { color: "#e2483d", level: "red" };
};

const coordFromValue = (lng, lat) => {
    const lngNum = Number(lng);
    const latNum = Number(lat);
    if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) return null;
    if (lngNum === 0 && latNum === 0) return null;
    return [lngNum, latNum];
};

export default function RouteNetworkMap({ routes = [], height = VIEW_HEIGHT }) {
    const model = useMemo(() => {
        const edges = [];
        const coordByIata = new Map();
        const referenced = new Set();

        const register = (iata, lng, lat) => {
            referenced.add(iata);
            if (coordByIata.has(iata)) return;
            const coord = coordFromValue(lng, lat) || AIRPORT_COORDS[iata];
            if (coord) coordByIata.set(iata, coord);
        };

        routes.forEach((route) => {
            const parsed = parseRouteCode(route.route_code);
            if (!parsed) return;
            register(parsed.origin, route.departure_longitude, route.departure_latitude);
            register(parsed.destination, route.arrival_longitude, route.arrival_latitude);
            edges.push({
                key: route.route_code,
                ...parsed,
                legCount: Number(route.actual_leg_count) || 0,
                completionRate: route.completion_rate,
            });
        });

        // 仅保留两端都有真实经纬度的航线；缺坐标的机场不再随机落点，直接计为未定位。
        const locatedEdges = edges.filter((edge) => coordByIata.has(edge.origin) && coordByIata.has(edge.destination));
        const unlocatedCount = Array.from(referenced).filter((iata) => !coordByIata.has(iata)).length;

        if (!locatedEdges.length) {
            return { edges: [], nodes: new Map(), unlocatedCount };
        }

        const usedAirports = new Set();
        locatedEdges.forEach((edge) => {
            usedAirports.add(edge.origin);
            usedAirports.add(edge.destination);
        });
        const usedCoords = Array.from(usedAirports).map((iata) => coordByIata.get(iata));
        const lngs = usedCoords.map((c) => c[0]);
        const lats = usedCoords.map((c) => c[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);

        // 等比缩放（经纬度同一比例尺）+ 居中留白：保持机场之间真实的相对位置和方向，
        // 只是不按大圆航线、距离非等距。
        const spanLng = Math.max(maxLng - minLng, 1e-6);
        const spanLat = Math.max(maxLat - minLat, 1e-6);
        const availW = VIEW_WIDTH - PAD * 2;
        const availH = height - PAD * 2;
        const scale = Math.min(availW / spanLng, availH / spanLat);
        const offsetX = (VIEW_WIDTH - spanLng * scale) / 2;
        const offsetY = (height - spanLat * scale) / 2;
        const project = (lng, lat) => ({
            x: offsetX + (lng - minLng) * scale,
            y: offsetY + (maxLat - lat) * scale,
        });

        const degree = new Map();
        locatedEdges.forEach((edge) => {
            degree.set(edge.origin, (degree.get(edge.origin) || 0) + 1);
            degree.set(edge.destination, (degree.get(edge.destination) || 0) + 1);
        });

        const nodes = new Map();
        usedAirports.forEach((iata) => {
            const coord = coordByIata.get(iata);
            nodes.set(iata, {
                iata,
                ...project(coord[0], coord[1]),
                degree: degree.get(iata) || 0,
            });
        });

        // 真实经纬度会让密集区域（如华东、欧洲）的机场点叠在一起。这里在保持大致相对
        // 位置的前提下做若干轮排斥松弛，把挨得太近的点推开，避免重叠（不再严格按比例）。
        const nodeArr = Array.from(nodes.values());
        const minDist = 34;
        for (let iter = 0; iter < 80; iter += 1) {
            for (let a = 0; a < nodeArr.length; a += 1) {
                for (let b = a + 1; b < nodeArr.length; b += 1) {
                    const na = nodeArr[a];
                    const nb = nodeArr[b];
                    let dx = nb.x - na.x;
                    let dy = nb.y - na.y;
                    let dist = Math.hypot(dx, dy);
                    if (dist < 1e-3) {
                        dx = a % 2 === 0 ? 1 : -1;
                        dy = b % 2 === 0 ? 1 : -1;
                        dist = Math.hypot(dx, dy);
                    }
                    if (dist < minDist) {
                        const push = (minDist - dist) / 2;
                        const ux = dx / dist;
                        const uy = dy / dist;
                        na.x -= ux * push;
                        na.y -= uy * push;
                        nb.x += ux * push;
                        nb.y += uy * push;
                    }
                }
            }
            nodeArr.forEach((node) => {
                node.x = Math.max(PAD, Math.min(VIEW_WIDTH - PAD, node.x));
                node.y = Math.max(PAD, Math.min(height - PAD, node.y));
            });
        }

        const maxLegCount = Math.max(...locatedEdges.map((edge) => edge.legCount), 1);
        const renderEdges = locatedEdges
            .map((edge) => {
                const from = nodes.get(edge.origin);
                const to = nodes.get(edge.destination);
                if (!from || !to) return null;
                const tone = completionTone(edge.completionRate);
                const width = 1 + (edge.legCount / maxLegCount) * 4;
                const mx = (from.x + to.x) / 2;
                const my = (from.y + to.y) / 2;
                const dx = to.x - from.x;
                const dy = to.y - from.y;
                const norm = Math.hypot(dx, dy) || 1;
                const lift = Math.min(norm * 0.22, 70);
                const cx = mx - (dy / norm) * lift;
                const cy = my - (dx / norm) * lift;
                return {
                    key: edge.key,
                    path: `M ${from.x.toFixed(1)} ${from.y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${to.x.toFixed(1)} ${to.y.toFixed(1)}`,
                    color: tone.color,
                    width,
                };
            })
            .filter(Boolean);

        return { edges: renderEdges, nodes, unlocatedCount };
    }, [routes, height]);

    const nodeList = useMemo(() => Array.from(model.nodes.values()), [model.nodes]);

    if (!model.edges.length) {
        return (
            <div className="business-dashboard-map-empty">
                <span>当前筛选条件下暂无可解析的航线 OD 数据</span>
            </div>
        );
    }

    return (
        <div className="business-dashboard-map">
            <svg viewBox={`0 0 ${VIEW_WIDTH} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="航线网络态势图">
                {Array.from({ length: 7 }).map((_, i) => (
                    <line
                        key={`h-${i}`}
                        x1="0"
                        x2={VIEW_WIDTH}
                        y1={(height / 6) * i}
                        y2={(height / 6) * i}
                        stroke="rgba(20,40,70,0.05)"
                        strokeWidth="1"
                    />
                ))}
                {Array.from({ length: 13 }).map((_, i) => (
                    <line
                        key={`v-${i}`}
                        y1="0"
                        y2={height}
                        x1={(VIEW_WIDTH / 12) * i}
                        x2={(VIEW_WIDTH / 12) * i}
                        stroke="rgba(20,40,70,0.05)"
                        strokeWidth="1"
                    />
                ))}
                {model.edges.map((edge) => (
                    <g key={edge.key}>
                        <path d={edge.path} fill="none" stroke={edge.color} strokeOpacity="0.45" strokeWidth={edge.width} strokeLinecap="round" />
                        <path
                            className="business-dashboard-map-flow"
                            d={edge.path}
                            fill="none"
                            stroke={edge.color}
                            strokeWidth={Math.max(edge.width * 0.6, 1)}
                            strokeLinecap="round"
                            strokeDasharray="3 16"
                        />
                    </g>
                ))}
                {nodeList.map((node) => {
                    const r = 3 + Math.min(node.degree, 6) * 0.9;
                    return (
                        <g key={node.iata} className="business-dashboard-map-node">
                            <circle cx={node.x} cy={node.y} r={r + 4} fill="rgba(43,108,255,0.10)" />
                            <circle cx={node.x} cy={node.y} r={r} fill="#2b6cff" stroke="#ffffff" strokeWidth="1.5" />
                            <text x={node.x + r + 4} y={node.y + 3} fill="#1b2a41" fontSize="11" fontWeight="700">
                                {node.iata}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <div className="business-dashboard-map-legend">
                <span><i style={{ background: "#15a35a" }} />完成率 ≥95%</span>
                <span><i style={{ background: "#d98a0b" }} />90%–95%</span>
                <span><i style={{ background: "#e2483d" }} />&lt;90%</span>
                <span className="business-dashboard-map-legend-note">线宽 ∝ 航段数</span>
                {model.unlocatedCount > 0 ? (
                    <span className="business-dashboard-map-legend-note">{model.unlocatedCount} 个机场缺坐标未显示</span>
                ) : null}
            </div>
        </div>
    );
}
